/**
 * SearchScopeChips
 *
 * Chip-strip that sits under the SlideSearchBar and lets the user steer the
 * scope of keyword + AI search.
 *
 * Default state: a single "Global" chip (or one folder chip when the user is
 * navigated into a folder via the sidebar). The plus button opens a popover
 * with two lists — folders and saved presentations — and any selection adds
 * a chip. Each chip has an X to remove it.
 *
 * Strictly a presentation component: the parent owns the scope state and
 * decides whether the implicit "current folder" scope is in effect.
 */

import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SlideSenseCollection, SlideSenseFolder } from "../types"

import { BookOpen, Check, Folder, Globe, Plus, X } from "@/components/icons"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { buildTree, type FolderNode } from "../utils/folder-tree"

/** Flatten the folder tree depth-first, carrying depth so list rows can show
 *  the nesting via indent. Mirrors what the sidebar does. */
function flattenWithDepth(
  nodes: FolderNode[],
  depth = 0
): { folder: FolderNode["folder"]; depth: number }[] {
  const out: { folder: FolderNode["folder"]; depth: number }[] = []
  for (const n of nodes) {
    out.push({ folder: n.folder, depth })
    if (n.children.length > 0) out.push(...flattenWithDepth(n.children, depth + 1))
  }
  return out
}

export interface SearchScopeChipsProps {
  /** Explicit folder chips. */
  folderIds: string[]
  /** Explicit collection chips. */
  collectionIds: string[]
  /** Implicit folder scope from sidebar navigation. Shown as a non-removable
   *  chip when no explicit chips are set. Removing it requires switching
   *  back to "All slides" in the sidebar. */
  implicitFolderId: string | null
  /** All folders, used to look up names by id. */
  folders: SlideSenseFolder[]
  /** All collections the user owns, used to look up names by id. */
  collections: SlideSenseCollection[]
  onChange: (next: { folderIds: string[]; collectionIds: string[] }) => void
}

export function SearchScopeChips({
  folderIds,
  collectionIds,
  implicitFolderId,
  folders,
  collections,
  onChange,
}: SearchScopeChipsProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const folderById = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders])
  const collectionById = useMemo(() => new Map(collections.map((c) => [c.id, c])), [collections])

  // Depth-first walk of the folder tree so the popover list reflects the real
  // parent/child hierarchy (matches the sidebar) instead of dumping every
  // folder out at the same level.
  const folderRows = useMemo(() => flattenWithDepth(buildTree(folders)), [folders])

  const hasExplicitChips = folderIds.length > 0 || collectionIds.length > 0
  const implicitFolder = implicitFolderId ? folderById.get(implicitFolderId) : null

  const toggleFolder = (id: string) => {
    if (folderIds.includes(id)) {
      onChange({ folderIds: folderIds.filter((x) => x !== id), collectionIds })
    } else {
      // If the implicit folder gets re-selected explicitly, seed the chip
      // strip with it AND the new one so the user sees both at once.
      const seeded =
        implicitFolderId && !folderIds.includes(implicitFolderId) && id !== implicitFolderId
          ? [implicitFolderId, ...folderIds, id]
          : [...folderIds, id]
      onChange({ folderIds: seeded, collectionIds })
    }
  }

  const toggleCollection = (id: string) => {
    if (collectionIds.includes(id)) {
      onChange({ folderIds, collectionIds: collectionIds.filter((x) => x !== id) })
    } else {
      // Same seed-from-implicit-folder rule so the user doesn't lose their
      // current folder context by adding a collection chip.
      const seededFolders =
        implicitFolderId && !folderIds.includes(implicitFolderId) && !hasExplicitChips
          ? [implicitFolderId]
          : folderIds
      onChange({ folderIds: seededFolders, collectionIds: [...collectionIds, id] })
    }
  }

  const removeFolder = (id: string) => {
    onChange({ folderIds: folderIds.filter((x) => x !== id), collectionIds })
  }
  const removeCollection = (id: string) => {
    onChange({ folderIds, collectionIds: collectionIds.filter((x) => x !== id) })
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Default chip — shown only when no explicit chips are set */}
      {!hasExplicitChips && !implicitFolder && (
        <ScopeChip icon={<Globe className="h-3 w-3" />} label={t("slideSense.scope.global")} />
      )}
      {!hasExplicitChips && implicitFolder && (
        <ScopeChip
          icon={<Folder className="h-3 w-3" />}
          label={implicitFolder.name}
          title={t("slideSense.scope.implicitFolderHint")}
        />
      )}

      {/* Explicit folder chips */}
      {folderIds.map((id) => {
        const folder = folderById.get(id)
        if (!folder) return null
        return (
          <ScopeChip
            key={`f-${id}`}
            icon={<Folder className="h-3 w-3" />}
            label={folder.name}
            onRemove={() => removeFolder(id)}
          />
        )
      })}

      {/* Explicit collection chips */}
      {collectionIds.map((id) => {
        const collection = collectionById.get(id)
        if (!collection) return null
        return (
          <ScopeChip
            key={`c-${id}`}
            icon={<BookOpen className="h-3 w-3" />}
            label={collection.name}
            onRemove={() => removeCollection(id)}
          />
        )
      })}

      {/* Plus → popover with folder + collection pickers */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            aria-label={t("slideSense.scope.addScope")}
            className="border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 inline-flex h-6 items-center gap-1 rounded-md border border-dashed px-2 text-[11px] transition-colors"
            type="button"
          >
            <Plus className="h-3 w-3" />
            {t("slideSense.scope.addScope")}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <Command>
            <CommandInput placeholder={t("slideSense.scope.searchPlaceholder")} />
            <CommandList>
              <CommandEmpty>{t("slideSense.scope.noResults")}</CommandEmpty>
              {folderRows.length > 0 && (
                <CommandGroup heading={t("slideSense.folders")}>
                  {folderRows.map(({ folder: f, depth }) => {
                    const checked = folderIds.includes(f.id)
                    return (
                      <CommandItem
                        key={f.id}
                        // Include the depth in `value` so cmdk's substring filter
                        // still matches the visible name only (depth is invisible
                        // to the user but disambiguates same-named siblings under
                        // different parents).
                        value={`folder:${"  ".repeat(depth)}${f.name}`}
                        onSelect={() => toggleFolder(f.id)}
                      >
                        {/* Visual indent — 14px per depth level matches the
                            sidebar's nesting feel without overflowing the
                            popover at deeper levels. */}
                        <span
                          aria-hidden="true"
                          className="shrink-0"
                          style={{ width: `${depth * 14}px` }}
                        />
                        <Folder className="text-muted-foreground mr-2 h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <Check
                          className={cn("ml-2 h-3.5 w-3.5 shrink-0", !checked && "opacity-0")}
                        />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
              {folders.length > 0 && collections.length > 0 && <CommandSeparator />}
              {collections.length > 0 && (
                <CommandGroup heading={t("slideSense.myPresentations")}>
                  {collections.map((c) => {
                    const checked = collectionIds.includes(c.id)
                    return (
                      <CommandItem
                        key={c.id}
                        value={`collection:${c.name}`}
                        onSelect={() => toggleCollection(c.id)}
                      >
                        <BookOpen className="text-muted-foreground mr-2 h-3.5 w-3.5" />
                        <span className="flex-1 truncate">{c.name}</span>
                        <Check className={cn("ml-2 h-3.5 w-3.5", !checked && "opacity-0")} />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* "Clear all" — only visible when explicit chips are set */}
      {hasExplicitChips && (
        <button
          className="text-muted-foreground hover:text-foreground text-[11px] hover:underline"
          type="button"
          onClick={() => onChange({ folderIds: [], collectionIds: [] })}
        >
          {t("slideSense.scope.clear")}
        </button>
      )}
    </div>
  )
}

function ScopeChip({
  icon,
  label,
  onRemove,
  title,
}: {
  icon: React.ReactNode
  label: string
  onRemove?: () => void
  title?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      className="border-border bg-muted/40 text-foreground inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[11px]"
      title={title}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="max-w-[140px] truncate">{label}</span>
      {onRemove && (
        <button
          aria-label={t("slideSense.scope.remove", { name: label })}
          className="text-muted-foreground hover:text-destructive ml-0.5 inline-flex items-center"
          type="button"
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}
